import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Bucket = {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
};

type ByModel = Bucket & { model: string };
type Daily = { day: string; calls: number; cost_usd: number };

type Balance = {
  month: Bucket;
  last_30: Bucket;
  by_model: ByModel[];
  daily: Daily[];
};

export default function AdminAiUsage() {
  const { user } = useAuth();
  const [data, setData] = useState<Balance | null>(null);
  const [loading, setLoading] = useState(true);

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  useEffect(() => {
    api
      .get<Balance>('/ai/usage/balance')
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="page-header">
        <h1>Consommation IA</h1>
      </div>

      {loading && <div>Chargement…</div>}
      {data && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <Stat label="Coût ce mois-ci" value={`${(data.month.cost_usd ?? 0).toFixed(2)} USD`} />
            <Stat label="Appels ce mois-ci" value={String(data.month.calls ?? 0)} />
            <Stat
              label="Tokens d'entrée (mois)"
              value={(data.month.input_tokens ?? 0).toLocaleString('fr-CH')}
            />
            <Stat
              label="Tokens de sortie (mois)"
              value={(data.month.output_tokens ?? 0).toLocaleString('fr-CH')}
            />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Par modèle (mois en cours)</div>
            {data.by_model.length === 0 && <div className="card-meta">Aucun appel ce mois.</div>}
            {data.by_model.map((m) => (
              <div
                key={m.model}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 120px 120px 120px',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--c-border)',
                  alignItems: 'center',
                  fontSize: 13,
                }}
              >
                <div style={{ fontFamily: 'monospace' }}>{m.model}</div>
                <div>{m.calls}</div>
                <div>{m.input_tokens.toLocaleString('fr-CH')} in</div>
                <div>{m.output_tokens.toLocaleString('fr-CH')} out</div>
                <div style={{ fontWeight: 600 }}>{m.cost_usd.toFixed(4)} USD</div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-title">30 derniers jours</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, marginTop: 12 }}>
              {data.daily.length === 0 && <div className="card-meta">Aucune donnée.</div>}
              {data.daily.map((d) => {
                const max = Math.max(0.01, ...data.daily.map((x) => x.cost_usd));
                const h = Math.max(2, (d.cost_usd / max) * 100);
                return (
                  <div
                    key={d.day}
                    title={`${d.day} — ${d.calls} appels — ${d.cost_usd.toFixed(4)} USD`}
                    style={{
                      flex: 1,
                      height: `${h}%`,
                      background: 'var(--c-sage)',
                      borderRadius: 3,
                      minWidth: 4,
                    }}
                  />
                );
              })}
            </div>
            <div className="card-meta" style={{ fontSize: 11, marginTop: 8 }}>
              Hauteur = coût USD du jour. Total 30j :{' '}
              {(data.last_30.cost_usd ?? 0).toFixed(2)} USD ({data.last_30.calls ?? 0} appels).
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="card-meta" style={{ fontSize: 11 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}
